import BodyCard from '@/Components/BodyCard'
import Loader from '@/Components/Common/Loader'
import SearchableDropdown from '@/Components/Common/SearchableDropdown'
import HeaderCard from '@/Components/HeaderCard'
import { Button } from '@/Components/ui/button'
import { Form, FormField, FormItem, FormLabel } from '@/Components/ui/form'
import { Input } from '@/Components/ui/input'
import React, { useEffect, useReducer, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLoaderData, useNavigate } from 'react-router-dom'
import GroupConfigurationRole from '@/assets/data/GroupConfigurationRole'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select'
import TextLabel from '@/Components/Common/TextLabel'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/Components/ui/table'
import { Checkbox } from '@/Components/ui/checkbox'
import NoDataFound from '@/Components/Common/NoDataFound'
import MultipleSelector from '@/Components/Common/MultipleSelector'
import AssessmentTypeService from '@/Service/AssessmentTypeService'
import UserService from '@/Service/UserService'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'react-toastify'
import MitemPayorTypeService from '@/Service/MitemPayorTypeService'
import GroupConfigurationService from '@/Service/GroupConfigurationService'
import { Popover, PopoverContent, PopoverTrigger } from '@/Components/ui/popover'
import { format, setDate } from 'date-fns'
import { cn } from '@/lib/utils'
import { Calendar } from '@/Components/ui/calendar'

import { CalendarIcon } from 'lucide-react'
import Constant from '@/utils/Constant'

const UpdateGroupConfigurations = () => {
    const formRef = useRef(null)
    const navigate = useNavigate()
    const { payorType, assessmentType, groupConfiguration, employee } = useLoaderData()

    useEffect(() => {
        const { employee_table, group_functionality_table, group_name, role, group_date_table } = groupConfiguration
        form.reset({ group_name, role })
        updateEvent({
            groupFunctionalityTable: group_functionality_table.map((table) => {
                table['checkbox'] = false
                table['payor_type'] = Array.isArray(table['payor_type']) ? table['payor_type'] : table['payor_type'].split(',')
                return table
            }),
            employeeTable: employee_table.map((table) => {
                table['checkbox'] = false
                return table
            }),
            dateTable: group_date_table.map((table) => {
                table['checkbox'] = false
                return table
            }),
            currentRole: role,
        })
    }, [groupConfiguration])

    const formSchema = z.object({
        group_name: z.string().min(1, {
            message: 'Group Name is required',
        }),
        role: z.string().min(1, {
            message: 'Role is required',
        }),
    })
    const [event, updateEvent] = useReducer(
        (prev, next) => {
            if (next?.isAllCheckGroupFunctionalityTable !== undefined) {
                prev.groupFunctionalityTable = prev.groupFunctionalityTable.map((group) => {
                    group['checkbox'] = next.isAllCheckGroupFunctionalityTable
                    return group
                })
            }
            return { ...prev, ...next }
        },
        {
            isSaving: false,
            currentRole: '',
            clients: [],
            payorTypes: [],
            assessmentType: [],
            employee: [],
            groupFunctionalityTable: [],
            employeeTable: [],
            isAllCheckGroupFunctionalityTable: false,
            isAllCheckDateTable: false,
            isAllCheckEmployeeTable: false,
        }
    )

    const form = useForm({
        shouldFocusError: false,
        resolver: zodResolver(formSchema),
        defaultValues: {
            group_name: '',
            role: '',
        },
    })
    const handleSaveClick = () => {
        if (formRef.current) {
            formRef.current.requestSubmit()
        }
    }

    const onSubmit = async (data) => {
        try {
            const groupMap = new Map()
            const seenEmails = new Set()
            const seenStartDates = new Set()
            const seenEndDates = new Set()
            const seenDatePairs = new Set()

            const { groupFunctionalityTable = [], employeeTable = [], dateTable = [] } = event

            if (!groupFunctionalityTable.length) {
                toast.error('Group Functionality is required')
                return false
            }

            groupFunctionalityTable.forEach((table) => {
                if (table['payor_type'].includes('All Payor Types')) {
                    groupMap.set('All Payor Types' + '-' + table['assessment_type'], 1)
                }
            })
            let prevAssessmentType = ''
            let allPayorTypeCount = 1

            for (const row of groupFunctionalityTable) {
                const { payor_type = [], assessment_type, target } = row

                if (!payor_type.length) {
                    toast.error('Payor Type is required')
                    return false
                }

                if (!assessment_type) {
                    toast.error('Assessment is required')
                    return false
                }

                if (!target || target == 0) {
                    toast.error('Target should not be empty or 0')
                    return false
                }

                // Reset count when assessment_type changes
                if (prevAssessmentType !== assessment_type) {
                    allPayorTypeCount = 1
                    prevAssessmentType = assessment_type
                }

                payor_type.forEach((type) => {
                    const key = `${type}-${assessment_type}`
                    const allKey = `All Payor Types-${assessment_type}`

                    if (type === 'All Payor Types' && allPayorTypeCount === 1) {
                        allPayorTypeCount++
                        return
                    }

                    if (groupMap.has(key)) {
                        groupMap.set(key, groupMap.get(key) + 1)
                    } else {
                        // If 'All Payor Types' exists, bump its count
                        if (groupMap.has(allKey)) {
                            groupMap.set(allKey, groupMap.get(allKey) + 1)
                        }
                        groupMap.set(key, 1)
                    }
                })
            }

            // Second pass: check for conflicts and duplicates
            for (const row of groupFunctionalityTable) {
                const { payor_type = [], assessment_type } = row
                const allKey = `All Payor Types-${assessment_type}`

                if (groupMap.has(allKey) && groupMap.get(allKey) > 1) {
                    toast.error(`We found all payor types linked to the '${assessment_type}' assessment. Please remove the extra ones.`)
                    return false
                }

                for (const type of payor_type) {
                    const key = `${type}-${assessment_type}`
                    if (groupMap.has(key) && groupMap.get(key) > 1) {
                        toast.error(`Duplicate Group Functionality Configuration found: ${key}`)
                        return false
                    }
                }
            }

            // Validate employee table
            for (const { employee_email, employee_name } of employeeTable) {
                if (!employee_email) {
                    toast.error('Employee email is required')
                    return false
                }
                if (!employee_name) {
                    toast.error('Employee name is required')
                    return false
                }
                if (seenEmails.has(employee_email)) {
                    toast.error(`Duplicate email found: ${employee_email}`)
                    return false
                }
                seenEmails.add(employee_email)
            }

            for (const { start_date, end_date, employee_email } of dateTable) {
                // Check for missing values
                if (!start_date) {
                    toast.error('Start date is required')
                    return false
                }

                if (!end_date) {
                    toast.error('End date is required')
                    return false
                }

                // Check for duplicate start_date
                if (seenStartDates.has(start_date)) {
                    toast.error(`Duplicate start date found: ${start_date} (Email: ${employee_email})`)
                    return false
                }

                // Check for duplicate end_date
                if (seenEndDates.has(end_date)) {
                    toast.error(`Duplicate end date found: ${end_date} (Email: ${employee_email})`)
                    return false
                }

                // Check for duplicate start_date + end_date pair
                const pairKey = `${start_date}_${end_date}`
                if (seenDatePairs.has(pairKey)) {
                    toast.error(`Duplicate date range found: ${start_date} to ${end_date} (Email: ${employee_email})`)
                    return false
                }

                // Add to seen sets
                seenStartDates.add(start_date)
                seenEndDates.add(end_date)
                seenDatePairs.add(pairKey)
            }

            data.group_configtion = groupFunctionalityTable
            data.employee_data = employeeTable
            data.group_date_table = dateTable
            data.name = data.group_name
            delete data.group_name
            delete data.role
            const groupConfig = await GroupConfigurationService.updateGroupConfigurations(data)
            if (groupConfig.data.success) {
                navigate('.', { replace: true })
            }
        } catch (err) {
            console.log('err', err)
        }
    }

    function onError(errors, e) {
        console.log('errors : ', errors)
        const errorKeys = Object.keys(errors)
        if (errorKeys.length > 0 && errors[errorKeys[0]]?.message) {
            toast.error(errors[errorKeys[0]].message)
        } else {
            toast.error('Something went wrong.')
        }
    }

    return (
        <div>
            <HeaderCard>
                <div className="flex items-center justify-end gap-2">
                    <Button
                        className="bg-primary-purpal hover:bg-primary-purpal px-4"
                        onClick={handleSaveClick}
                        disabled={event.isSaving}
                        iconType="Save"
                    >
                        Save {event.isSaving && <Loader fullScreen={false} />}
                    </Button>
                </div>
            </HeaderCard>
            <BodyCard>
                <Form {...form} className="">
                    <form ref={formRef} onSubmit={form.handleSubmit(onSubmit, onError)} className="">
                        <div className="grid grid-cols-3 gap-y-2 gap-x-6">
                            <FormField
                                control={form.control}
                                name="group_name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required={true}>Group Name</FormLabel>
                                        <Input {...field} placeholder="Group Name" disabled={true} />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="role"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required={true}>Role</FormLabel>
                                        <Input {...field} placeholder="Role" disabled={true} />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-1 gap-y-2 gap-x-6 mt-5">
                            <div>
                                <TextLabel required={true} text="Date" />
                                <Table className="mt-2">
                                    <TableHeader>
                                        <TableRow className="border">
                                            <TableHead className="w-[5%] font-bold border">
                                                <Checkbox
                                                    checked={event.isAllCheckDateTable}
                                                    onCheckedChange={(isChecked) => {
                                                        updateEvent({ isAllCheckDateTable: isChecked })
                                                    }}
                                                />
                                            </TableHead>
                                            <TableHead className="font-bold border">Sr.No</TableHead>
                                            <TableHead className="font-bold border">Start Date</TableHead>
                                            <TableHead className="font-bold border">End Date</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {event?.dateTable?.length == 0 ? (
                                            <TableRow key="noDataFound" className="border">
                                                <TableCell className="border" colSpan="6">
                                                    <NoDataFound />
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            event?.dateTable?.map((row, currentIndex) => (
                                                <TableRow key={currentIndex} className="border">
                                                    <TableCell className="border">
                                                        <Checkbox
                                                            onCheckedChange={(isChecked) =>
                                                                updateEvent({
                                                                    dateTable: event.dateTable.map((dates, i) => {
                                                                        if (i == currentIndex) {
                                                                            dates['checkbox'] = isChecked
                                                                        }
                                                                        return dates
                                                                    }),
                                                                })
                                                            }
                                                            checked={event.isAllCheckDateTable ? event.isAllCheckDateTable : row.checkbox}
                                                            value={event.isAllCheckDateTable ? event.isAllCheckDateTable : row.checkbox}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border">{parseInt(1 + currentIndex)}</TableCell>

                                                    <TableCell className="border px-0">
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    variant={'outline'}
                                                                    className={cn('w-full pl-3 text-left font-normal border-none')}
                                                                >
                                                                    {row.start_date ? format(row.start_date, Constant.DATE_FORMAT) : 'Select date'}
                                                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                <Calendar
                                                                    mode="single"
                                                                    selected={row.start_date}
                                                                    onSelect={(date) => {
                                                                        updateEvent({
                                                                            dateTable: event.dateTable.map((dates, i) => {
                                                                                if (i == currentIndex) {
                                                                                    dates['start_date'] = date
                                                                                }
                                                                                return dates
                                                                            }),
                                                                        })
                                                                    }}
                                                                    captionLayout="dropdown"
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </TableCell>
                                                    <TableCell className="border px-0">
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button
                                                                    variant={'outline'}
                                                                    className={cn('w-full pl-3 text-left font-normal border-none')}
                                                                >
                                                                    {row.end_date ? format(row.end_date, Constant.DATE_FORMAT) : 'Select date'}
                                                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent className="w-auto p-0" align="start">
                                                                <Calendar
                                                                    mode="single"
                                                                    selected={row.end_date}
                                                                    onSelect={(date) => {
                                                                        if (date && row.start_date && date < row.start_date) {
                                                                            toast.error('End Date should be greater than or equal to Start Date')
                                                                            return
                                                                        }
                                                                        updateEvent({
                                                                            dateTable: event.dateTable.map((dates, i) => {
                                                                                if (i == currentIndex) {
                                                                                    dates['end_date'] = date
                                                                                }
                                                                                return dates
                                                                            }),
                                                                        })
                                                                    }}
                                                                    captionLayout="dropdown"
                                                                />
                                                            </PopoverContent>
                                                        </Popover>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                                <div className="mt-2 flex gap-1">
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            console.log('indatetable', event.dateTable)
                                            updateEvent({
                                                dateTable: [
                                                    ...event.dateTable,
                                                    {
                                                        id: event.dateTable.length == 0 ? 1 : event.dateTable[event.dateTable.length - 1]['id'] + 1,
                                                        checkbox: false,
                                                        start_date: '',
                                                        end_date: '',
                                                    },
                                                ],
                                            })
                                        }}
                                        className=" hover:bg-gray-200 bg-gray-200 border-gray-800 text-gray-600 text-[12px]"
                                    >
                                        Add Row
                                    </Button>

                                    {event?.dateTable?.length > 0 &&
                                        !event.isAllCheckDateTable &&
                                        event.dateTable.filter((row) => row.checkbox).length > 0 && (
                                            <Button
                                                type="button"
                                                className="bg-primary-red ml-1 font-normal"
                                                onClick={() =>
                                                    updateEvent({
                                                        dateTable: event.dateTable.filter((dates) => !dates.checkbox),
                                                    })
                                                }
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    {event?.dateTable?.length > 0 && event.isAllCheckDateTable && (
                                        <Button
                                            type="button"
                                            className="bg-primary-red ml-1 font-normal"
                                            onClick={() => updateEvent({ isAllCheckDateTable: false, dateTable: [] })}
                                        >
                                            Delete All
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-y-2 gap-x-6 mt-5">
                            <div>
                                <TextLabel required={true} text="Group Functionality Table" />
                                <Table className="mt-2">
                                    <TableHeader>
                                        <TableRow className="border">
                                            <TableHead className="w-[5%] font-bold border">
                                                <Checkbox
                                                    checked={event.isAllCheckGroupFunctionalityTable}
                                                    onCheckedChange={(isChecked) => {
                                                        updateEvent({ isAllCheckGroupFunctionalityTable: isChecked })
                                                    }}
                                                />
                                            </TableHead>
                                            <TableHead className="font-bold w-[10%] border">Sr.No</TableHead>
                                            <TableHead className="font-bold w-[50%] border">Payor Type</TableHead>
                                            <TableHead className="font-bold w-[25%] border">Assessment Type</TableHead>
                                            <TableHead className="font-bold w-[10%] border">Target</TableHead>
                                            <TableHead className="font-bold w-[10%] border">Total</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {event.groupFunctionalityTable.length == 0 ? (
                                            <TableRow key="noDataFound" className="border">
                                                <TableCell className="border" colSpan="6">
                                                    <NoDataFound />
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            event.groupFunctionalityTable.map((row, currentIndex) => (
                                                <TableRow key={currentIndex} className="border">
                                                    <TableCell className="border">
                                                        <Checkbox
                                                            onCheckedChange={(isChecked) =>
                                                                updateEvent({
                                                                    groupFunctionalityTable: event.groupFunctionalityTable.map((roles, i) => {
                                                                        if (i == currentIndex) {
                                                                            roles['checkbox'] = isChecked
                                                                        }
                                                                        return roles
                                                                    }),
                                                                })
                                                            }
                                                            checked={row.checkbox}
                                                            value={row.checkbox}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border">{parseInt(1 + currentIndex)}</TableCell>

                                                    <TableCell className="border px-0">
                                                        <MultipleSelector
                                                            placeHolder="Payor Type"
                                                            selectedValue={row.payor_type}
                                                            onChange={(val) => {
                                                                updateEvent({
                                                                    groupFunctionalityTable: event.groupFunctionalityTable.map((group, i) => {
                                                                        if (i == currentIndex) {
                                                                            group['payor_type'] = val
                                                                        }
                                                                        return group
                                                                    }),
                                                                })
                                                            }}
                                                            items={payorType}
                                                            label="name"
                                                            className="border-none w-full outline-none ring-0 focus-visible:ring-0 active:ring-0"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border px-0">
                                                        <SearchableDropdown
                                                            selectedVal={row.assessment_type}
                                                            options={assessmentType}
                                                            handleChange={(val) => {
                                                                updateEvent({
                                                                    groupFunctionalityTable: event.groupFunctionalityTable.map((groups, i) => {
                                                                        if (i == currentIndex) {
                                                                            groups['assessment_type'] = val
                                                                        }
                                                                        return groups
                                                                    }),
                                                                })
                                                            }}
                                                            className="border-none w-full outline-none ring-0 focus-visible:ring-0 shadow-none"
                                                            placeholder="Assessment Type"
                                                            label="assesment_type"
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border px-0">
                                                        <Input
                                                            className="border-none shadow-none focus-visible:ring-0 focus:border-0 focus:shadow-none focus:border-none"
                                                            type="number"
                                                            min={0}
                                                            value={row.target}
                                                            placeholder="Target"
                                                            onChange={(e) => {
                                                                updateEvent({
                                                                    groupFunctionalityTable: event.groupFunctionalityTable.map((group, i) => {
                                                                        if (i == currentIndex) {
                                                                            group['target'] = e.target.value
                                                                            group['total_target'] =
                                                                                parseInt(e.target.value) * event.employeeTable.length
                                                                        }
                                                                        return group
                                                                    }),
                                                                })
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key == 'Tab' &&
                                                                    currentIndex == event.groupFunctionalityTable.length - 1 &&
                                                                    (event.groupFunctionalityTable[event.groupFunctionalityTable.length - 1][
                                                                        'payor_type'
                                                                    ].length > 0 ||
                                                                        event.groupFunctionalityTable[event.groupFunctionalityTable.length - 1][
                                                                            'assessment_type'
                                                                        ] !== '' ||
                                                                        event.groupFunctionalityTable[event.groupFunctionalityTable.length - 1][
                                                                            'target'
                                                                        ] !== '' ||
                                                                        event.groupFunctionalityTable[event.groupFunctionalityTable.length - 1][
                                                                            'target'
                                                                        ] !== 0)
                                                                ) {
                                                                    updateEvent({
                                                                        groupFunctionalityTable: [
                                                                            ...event.groupFunctionalityTable,
                                                                            {
                                                                                id:
                                                                                    event.groupFunctionalityTable.length == 0
                                                                                        ? 1
                                                                                        : event.groupFunctionalityTable[
                                                                                              event.groupFunctionalityTable.length - 1
                                                                                          ]['id'] + 1,
                                                                                checkbox: false,
                                                                                payor_type: [],
                                                                                assessment_type: '',
                                                                                total_target: 0,
                                                                            },
                                                                        ],
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border px-0">
                                                        <Input
                                                            className="border-none shadow-none focus-visible:ring-0 focus:border-0 focus:shadow-none focus:border-none"
                                                            type="number"
                                                            disabled={true}
                                                            min={0}
                                                            value={row.total_target}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                                <div className="mt-2 flex gap-1">
                                    <Button
                                        type="button"
                                        onClick={() =>
                                            updateEvent({
                                                groupFunctionalityTable: [
                                                    ...event.groupFunctionalityTable,
                                                    {
                                                        id:
                                                            event.groupFunctionalityTable.length == 0
                                                                ? 1
                                                                : event.groupFunctionalityTable[event.groupFunctionalityTable.length - 1]['id'] + 1,
                                                        checkbox: false,
                                                        payor_type: [],
                                                        assessment_type: '',
                                                        target: 0,
                                                        total_target: 0,
                                                    },
                                                ],
                                            })
                                        }
                                        className=" hover:bg-gray-200 bg-gray-200 border-gray-800 text-gray-600 text-[12px]"
                                    >
                                        Add Row
                                    </Button>

                                    {event.groupFunctionalityTable.length > 0 &&
                                        !event.isAllCheckGroupFunctionalityTable &&
                                        event.groupFunctionalityTable.filter((row) => row.checkbox).length > 0 && (
                                            <Button
                                                type="button"
                                                className="bg-primary-red ml-1 font-normal"
                                                onClick={() =>
                                                    updateEvent({
                                                        groupFunctionalityTable: event.groupFunctionalityTable.filter((roles) => !roles.checkbox),
                                                    })
                                                }
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    {event.groupFunctionalityTable.length > 0 && event.isAllCheckGroupFunctionalityTable && (
                                        <Button
                                            type="button"
                                            className="bg-primary-red ml-1 font-normal"
                                            onClick={() => updateEvent({ isAllCheckGroupFunctionalityTable: false, groupFunctionalityTable: [] })}
                                        >
                                            Delete All
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 gap-y-2 gap-x-6 mt-5">
                            <div>
                                <TextLabel text="Employee Table" />
                                <Table className="mt-2">
                                    <TableHeader>
                                        <TableRow className="border">
                                            <TableHead className="w-[5%] font-bold border">
                                                <Checkbox
                                                    checked={event.isAllCheckEmployeeTable}
                                                    onCheckedChange={(isChecked) => {
                                                        updateEvent({ isAllCheckEmployeeTable: isChecked })
                                                    }}
                                                />
                                            </TableHead>
                                            <TableHead className="font-bold w-[7%] border">Sr.No</TableHead>
                                            <TableHead className="font-bold border">Employee Email</TableHead>
                                            <TableHead className="font-bold border">Employee Name</TableHead>
                                            <TableHead className="font-bold w-[7%] border">Sampling</TableHead>
                                            <TableHead className="font-bold w-[7%] border">Priority</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {event.employeeTable.length == 0 ? (
                                            <TableRow key="noDataFound" className="border">
                                                <TableCell className="border" colSpan="6">
                                                    <NoDataFound />
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            event.employeeTable.map((row, currentIndex) => (
                                                <TableRow key={currentIndex} className="border">
                                                    <TableCell className="border">
                                                        <Checkbox
                                                            onCheckedChange={(isChecked) =>
                                                                updateEvent({
                                                                    employeeTable: event.employeeTable.map((employee, i) => {
                                                                        if (i == currentIndex) {
                                                                            employee['checkbox'] = isChecked
                                                                        }
                                                                        return employee
                                                                    }),
                                                                })
                                                            }
                                                            checked={row.checkbox}
                                                            value={row.checkbox}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border">{parseInt(1 + currentIndex)}</TableCell>
                                                    <TableCell className="border px-0">
                                                        <SearchableDropdown
                                                            selectedVal={row.employee_email}
                                                            options={employee}
                                                            label="email"
                                                            handleChange={(val) => {
                                                                updateEvent({
                                                                    employeeTable: event.employeeTable.map((employee, i) => {
                                                                        if (i == currentIndex) {
                                                                            employee['employee_email'] = val.email
                                                                            employee['employee_name'] = val.full_name
                                                                        }
                                                                        return employee
                                                                    }),
                                                                })
                                                            }}
                                                            className="border-none w-full outline-none ring-0 focus-visible:ring-0"
                                                            placeholder="Employee Email"
                                                            type="GET_OBJ"
                                                            searchFilter={{ role: event.currentRole }}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border px-3">{row.employee_name}</TableCell>
                                                    <TableCell className="border px-0">
                                                        <Input
                                                            className=" border-none shadow-none focus-visible:ring-0 focus:border-0 focus:shadow-none focus:border-none"
                                                            value={row.sampling}
                                                            onChange={(e) => {
                                                                updateEvent({
                                                                    employeeTable: event.employeeTable.map((employee, i) => {
                                                                        if (i == currentIndex) {
                                                                            employee['sampling'] = e.target.value
                                                                        }
                                                                        return employee
                                                                    }),
                                                                })
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="border px-0">
                                                        <Input
                                                            className=" border-none shadow-none focus-visible:ring-0 focus:border-0 focus:shadow-none focus:border-none"
                                                            value={row.priority}
                                                            onChange={(e) => {
                                                                updateEvent({
                                                                    employeeTable: event.employeeTable.map((employee, i) => {
                                                                        if (i == currentIndex) {
                                                                            employee['priority'] = e.target.value
                                                                        }
                                                                        return employee
                                                                    }),
                                                                })
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (
                                                                    e.key == 'Tab' &&
                                                                    currentIndex == event.employeeTable.length - 1 &&
                                                                    (event.employeeTable[event.employeeTable.length - 1]['employee_email'] !== '' ||
                                                                        event.employeeTable[event.employeeTable.length - 1]['employee_name'] !== '' ||
                                                                        event.employeeTable[event.employeeTable.length - 1]['sampling'] !== '' ||
                                                                        event.employeeTable[event.employeeTable.length - 1]['priority'] !== '')
                                                                ) {
                                                                    updateEvent({
                                                                        employeeTable: [
                                                                            ...event.employeeTable,
                                                                            {
                                                                                id:
                                                                                    event.employeeTable.length == 0
                                                                                        ? 1
                                                                                        : event.employeeTable[event.employeeTable.length - 1]['id'] +
                                                                                          1,
                                                                                checkbox: false,
                                                                                employee_email: '',
                                                                                employee_name: '',
                                                                                sampling: 1,
                                                                                priority: 1,
                                                                            },
                                                                        ],
                                                                    })
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                                <div className="mt-2 flex gap-1">
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            if (employee.length == 0) {
                                                toast.error('Please select role first')
                                                return false
                                            }
                                            updateEvent({
                                                employeeTable: [
                                                    ...event.employeeTable,
                                                    {
                                                        id:
                                                            event.employeeTable.length == 0
                                                                ? 1
                                                                : event.employeeTable[event.employeeTable.length - 1]['id'] + 1,
                                                        checkbox: false,
                                                        employee_email: '',
                                                        employee_name: '',
                                                        sampling: 1,
                                                        priority: 1,
                                                    },
                                                ],
                                                groupFunctionalityTable: event.groupFunctionalityTable.map((group, i) => {
                                                    console.log('event.employeeTable.length', event.employeeTable.length, group.target, group)
                                                    return { ...group, total_target: (event.employeeTable.length + 1) * parseInt(group.target) }
                                                }),
                                            })
                                        }}
                                        className=" hover:bg-gray-200 bg-gray-200 border-gray-800 text-gray-600 text-[12px]"
                                    >
                                        Add Row
                                    </Button>

                                    {event.employeeTable.length > 0 &&
                                        !event.isAllCheckEmployeeTable &&
                                        event.employeeTable.filter((row) => row.checkbox).length > 0 && (
                                            <Button
                                                type="button"
                                                className="bg-primary-red ml-1 font-normal"
                                                onClick={() =>
                                                    updateEvent({
                                                        employeeTable: event.employeeTable.filter((roles) => !roles.checkbox),
                                                    })
                                                }
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    {event.employeeTable.length > 0 && event.isAllCheckEmployeeTable && (
                                        <Button
                                            type="button"
                                            className="bg-primary-red ml-1 font-normal"
                                            onClick={() => updateEvent({ isAllCheckEmployeeTable: false, employeeTable: [] })}
                                        >
                                            Delete All
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                </Form>
            </BodyCard>
        </div>
    )
}

export default UpdateGroupConfigurations
